/**
 * Build the 6-row × 7-column matrix of Date objects for a given (year, month).
 * Rows start on Monday. Days outside the target month are still real Date objects
 * (so we can render them greyed-out and keep grid alignment).
 *
 * All arithmetic is done in local time. Compare against ISO date strings
 * (`YYYY-MM-DD`) coming from the API to dodge DST/timezone shifts.
 */
import { useMemo } from "react";

const DAYS_IN_WEEK = 7;
const WEEKS_IN_GRID = 6;

export interface MonthMatrix {
  year: number;
  month: number;
  firstOfMonth: Date;
  lastOfMonthExclusive: Date;
  weeks: Date[][];
}

export function useMonthMatrix(year: number, month: number): MonthMatrix {
  return useMemo(() => {
    const firstOfMonth = new Date(year, month - 1, 1);
    const lastOfMonthExclusive = new Date(year, month, 1);

    const dayOfWeek = (firstOfMonth.getDay() + 6) % DAYS_IN_WEEK;
    const gridStart = new Date(year, month - 1, 1 - dayOfWeek);

    const weeks: Date[][] = [];
    let cursor = gridStart;
    for (let w = 0; w < WEEKS_IN_GRID; w++) {
      const row: Date[] = [];
      for (let d = 0; d < DAYS_IN_WEEK; d++) {
        row.push(new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate()));
        cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1);
      }
      weeks.push(row);
    }
    return { year, month, firstOfMonth, lastOfMonthExclusive, weeks };
  }, [year, month]);
}

export function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function addDays(iso: string, days: number): string {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + days);
  return toIsoDate(d);
}
